import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import './i18n';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CountryProvider } from './context/CountryContext';
import Layout from './components/Layout/Layout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import LeadList from './pages/Leads/LeadList';
import LeadForm from './pages/Leads/LeadForm';
import LeadDetail from './pages/Leads/LeadDetail';
import StudentList from './pages/Students/StudentList';
import StudentForm from './pages/Students/StudentForm';
import StudentDetail from './pages/Students/StudentDetail';
import StudentIntakeForm from './pages/Students/StudentIntakeForm';
import InterviewList from './pages/Interviews/InterviewList';
import InterviewForm from './pages/Interviews/InterviewForm';
import MockInterviewList from './pages/MockInterviews/MockInterviewList';
import MockInterviewForm from './pages/MockInterviews/MockInterviewForm';
import MeetingList from './pages/Meetings/MeetingList';
import ActivityList from './pages/Activities/ActivityList';
import NotificationList from './pages/Notifications/NotificationList';
import PaymentList from './pages/Payments/PaymentList';
import AgreementList from './pages/Agreements/AgreementList';
import MailboxList from './pages/Mailbox/MailboxList';
import EmailTemplateList from './pages/EmailTemplates/EmailTemplateList';
import MailAutoConfig from './pages/MailAutoConfig/MailAutoConfig';
import MailAutoJobForm from './pages/MailAutoConfig/MailAutoJobForm';
import UserList from './pages/Users/UserList';
import Settings from './pages/Settings/Settings';
import Profile from './pages/Profile/Profile';
import Chat from './pages/Chat/Chat';
import CompanyList from './pages/Companies/CompanyList';
import CompanyDetail from './pages/Companies/CompanyDetail';
import JobList from './pages/Jobs/JobList';
import JobDetail from './pages/Jobs/JobDetail';
import TicketList from './pages/Tickets/TicketList';
import TicketDetail from './pages/Tickets/TicketDetail';
import TicketNew from './pages/Tickets/TicketNew';
import ProspectList from './pages/Prospects/ProspectList';
import ProspectForm from './pages/Prospects/ProspectForm';
import ProspectDetail from './pages/Prospects/ProspectDetail';
import RefundRequestList from './pages/RefundRequests/RefundRequestList';
import RefundRequestDetail from './pages/RefundRequests/RefundRequestDetail';
const ProtectedRoute = ({ children, module }) => {
  const { user, loading, hasModuleAccess } = useAuth();

  if (loading) {
    return (
      <div className="loading-spinner" style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (module && !hasModuleAccess(module)) {
    const defaultPath = user.role === 'interviewer' ? '/interviews' : '/';
    return <Navigate to={defaultPath} replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-spinner" style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (user) {
    const defaultPath = user.role === 'interviewer' ? '/interviews' : '/';
    return <Navigate to={defaultPath} replace />;
  }

  return children;
};

const DashboardRedirect = () => {
  const { user, loading, hasModuleAccess } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'interviewer') return <Navigate to="/interviews" replace />;
  if (!hasModuleAccess('dashboard')) return <Navigate to="/interviews" replace />;
  return <Layout><Dashboard /></Layout>;
};

const IntakeLegacyRedirect = () => {
  const { token } = useParams();
  return <Navigate to={`/students/intake/${token}`} replace />;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

      <Route path="/" element={<DashboardRedirect />} />
      <Route path="/dashboard" element={<DashboardRedirect />} />

      <Route path="/prospects" element={<ProtectedRoute module="prospects"><ProspectList /></ProtectedRoute>} />
      <Route path="/prospects/new" element={<ProtectedRoute module="prospects"><ProspectForm /></ProtectedRoute>} />
      <Route path="/prospects/:id" element={<ProtectedRoute module="prospects"><ProspectDetail /></ProtectedRoute>} />
      <Route path="/prospects/:id/edit" element={<ProtectedRoute module="prospects"><ProspectForm /></ProtectedRoute>} />

      <Route path="/b2b-agencies" element={<ProtectedRoute module="b2b_agencies"><LeadList /></ProtectedRoute>} />
      <Route path="/b2b-agencies/new" element={<ProtectedRoute module="b2b_agencies"><LeadForm /></ProtectedRoute>} />
      <Route path="/b2b-agencies/:id" element={<ProtectedRoute module="b2b_agencies"><LeadDetail /></ProtectedRoute>} />
      <Route path="/b2b-agencies/:id/edit" element={<ProtectedRoute module="b2b_agencies"><LeadForm /></ProtectedRoute>} />
      <Route path="/leads/*" element={<Navigate to="/b2b-agencies" replace />} />

      <Route path="/students" element={<ProtectedRoute module="students"><StudentList /></ProtectedRoute>} />
      <Route path="/students/new" element={<ProtectedRoute module="students"><StudentForm /></ProtectedRoute>} />
      <Route path="/students/:id" element={<ProtectedRoute module="students"><StudentDetail /></ProtectedRoute>} />
      <Route path="/students/:id/edit" element={<ProtectedRoute module="students"><StudentForm /></ProtectedRoute>} />
      <Route path="/students/intake/:token" element={<StudentIntakeForm />} />
      <Route path="/student-intake/:token" element={<IntakeLegacyRedirect />} />

      <Route path="/interviews" element={<ProtectedRoute module="interviews"><InterviewList /></ProtectedRoute>} />
      <Route path="/interviews/new/:studentId?" element={<ProtectedRoute module="interviews"><InterviewForm /></ProtectedRoute>} />
      <Route path="/interviews/:id" element={<ProtectedRoute module="interviews"><InterviewForm /></ProtectedRoute>} />
      <Route path="/mock-interviews" element={<ProtectedRoute module="mock_interviews"><MockInterviewList /></ProtectedRoute>} />
      <Route path="/mock-interviews/new/:studentId?" element={<ProtectedRoute module="mock_interviews"><MockInterviewForm /></ProtectedRoute>} />
      <Route path="/mock-interviews/:id" element={<ProtectedRoute module="mock_interviews"><MockInterviewForm /></ProtectedRoute>} />

      <Route path="/meetings" element={<ProtectedRoute module="meetings"><MeetingList /></ProtectedRoute>} />
      <Route path="/activities" element={<ProtectedRoute module="activities"><ActivityList /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute module="notifications"><NotificationList /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute module="payments"><PaymentList /></ProtectedRoute>} />
      <Route path="/agreements" element={<ProtectedRoute module="agreements"><AgreementList /></ProtectedRoute>} />
      <Route path="/mailbox" element={<ProtectedRoute module="mailbox"><MailboxList /></ProtectedRoute>} />
      <Route path="/email-templates" element={<ProtectedRoute module="mailbox"><EmailTemplateList /></ProtectedRoute>} />
      <Route path="/mail-auto-config" element={<ProtectedRoute module="mail_auto_config"><MailAutoConfig /></ProtectedRoute>} />
      <Route path="/mail-auto-config/new" element={<ProtectedRoute module="mail_auto_config"><MailAutoJobForm /></ProtectedRoute>} />
      <Route path="/mail-auto-config/:id/edit" element={<ProtectedRoute module="mail_auto_config"><MailAutoJobForm /></ProtectedRoute>} />

      <Route path="/companies" element={<ProtectedRoute module="companies"><CompanyList /></ProtectedRoute>} />
      <Route path="/companies/:id" element={<ProtectedRoute module="companies"><CompanyDetail /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute module="jobs"><JobList /></ProtectedRoute>} />
      <Route path="/jobs/:id" element={<ProtectedRoute module="jobs"><JobDetail /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute module="chat"><Chat /></ProtectedRoute>} />
      <Route path="/tickets" element={<ProtectedRoute module="tickets"><TicketList /></ProtectedRoute>} />
      <Route path="/tickets/new" element={<ProtectedRoute module="tickets"><TicketNew /></ProtectedRoute>} />
      <Route path="/tickets/:id" element={<ProtectedRoute module="tickets"><TicketDetail /></ProtectedRoute>} />
      <Route path="/refund-requests" element={<ProtectedRoute module="refund_requests"><RefundRequestList /></ProtectedRoute>} />
      <Route path="/refund-requests/:id" element={<ProtectedRoute module="refund_requests"><RefundRequestDetail /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute module="users"><UserList /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute module="settings"><Settings /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const CountryWrap = ({ children }) => {
  const { user } = useAuth();
  return <CountryProvider user={user}>{children}</CountryProvider>;
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <CountryWrap>
        <AppRoutes />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </CountryWrap>
      </AuthProvider>
    </Router>
  );
};

export default App;
